import type {
  FundingBehavior,
  PlatformComparisonResult,
  PlatformOptionGroup,
  SupporterPaidIndicator,
} from '../models/fundraising-comparison.models';

export function selectedOptionSummary(
  result: PlatformComparisonResult,
  group: PlatformOptionGroup,
): string {
  if (group.input?.kind === 'percentage') {
    const rateBasisPoints = Number(result.optionSelections[group.id]);
    const label = Number.isInteger(rateBasisPoints)
      ? `${Number((rateBasisPoints / 100).toFixed(2))}%`
      : '';
    return group.summaryPrefix ? `${group.summaryPrefix}: ${label}` : label;
  }
  const selectedChoice = (group.choices ?? []).find(
    (choice) => choice.id === result.optionSelections[group.id],
  );
  const label = selectedChoice?.summaryLabel ?? selectedChoice?.label ?? '';
  return group.summaryPrefix ? `${group.summaryPrefix}: ${label}` : label;
}

export function configurationSummary(result: PlatformComparisonResult): string {
  const parts = result.optionGroups
    .map((group) => selectedOptionSummary(result, group))
    .filter(Boolean);
  const revenueShareItems = result.feeLineItems.filter(
    (item) =>
      item.includedInPrimaryResult &&
      item.paidBy === 'organization' &&
      item.category === 'revenue-share' &&
      item.amountCents > 0,
  );
  if (
    revenueShareItems.length > 0 &&
    result.feeLineItems
      .filter(
        (item) =>
          item.includedInPrimaryResult &&
          item.paidBy === 'organization' &&
          item.amountCents > 0,
      )
      .every((item) => item.category === 'revenue-share')
  ) {
    const labels = new Set(
      revenueShareItems.map((item) => item.label.split(':')[0]),
    );
    if (labels.size === 1) {
      parts.push(
        `${[...labels][0]}: ${Number(
          (result.effectiveOrganizationCostBasisPoints / 100).toFixed(2),
        )}%`,
      );
    }
  }
  return parts.join(' · ');
}

export function hasOptionalTip(result: PlatformComparisonResult): boolean {
  return result.feeRecipeRows.some((row) => row.id === 'optional-donor-tip');
}

export function selectedFundingKind(
  result: PlatformComparisonResult,
): FundingBehavior['kind'] | undefined {
  return result.optionGroups
    .map((group) =>
      (group.choices ?? []).find(
        (choice) => choice.id === result.optionSelections[group.id],
      ),
    )
    .find((choice) => choice?.fundingBehavior)?.fundingBehavior?.kind;
}

export function costModelSummary(result: PlatformComparisonResult): string {
  const configuredSummary = configurationSummary(result);
  if (configuredSummary) return configuredSummary;

  const organizationFeeItems = result.feeLineItems.filter(
    (item) =>
      item.includedInPrimaryResult &&
      item.paidBy === 'organization' &&
      item.amountCents > 0,
  );
  if (
    organizationFeeItems.length > 0 &&
    organizationFeeItems.every((item) => item.category === 'processing')
  ) {
    return 'Your organization pays processing';
  }
  if (result.organizationCostCents > 0) {
    return 'Your organization pays fundraising costs';
  }
  if (hasOptionalTip(result)) {
    return 'Optional donor tips';
  }
  if (result.donorPaidExtraCents > 0) {
    return 'Supporters pay fees';
  }
  return 'No fees to your organization or supporters';
}

export function organizationCostLabel(
  result: PlatformComparisonResult,
): string {
  const revenueShareItem = result.feeLineItems.find(
    (item) =>
      item.includedInPrimaryResult &&
      item.paidBy === 'organization' &&
      item.category === 'revenue-share',
  );
  return revenueShareItem?.label.split(':')[0] ?? 'Your organization pays';
}

export function usesRevenueShare(result: PlatformComparisonResult): boolean {
  return organizationCostLabel(result) !== 'Your organization pays';
}

export function showSupporterExtra(result: PlatformComparisonResult): boolean {
  return !result.donorPaidExtraEstimated || result.donorPaidExtraCents > 0;
}

export function supporterExtraLabel(result: PlatformComparisonResult): string {
  if (result.donorPaidExtraEstimated) return 'Supporters pay extra';
  if (selectedFundingKind(result) === 'optional-fee-coverage') {
    return 'Supporters may cover fees';
  }
  if (hasOptionalTip(result)) return 'Optional donor tips';
  return 'Supporters may pay extra';
}

export function overviewSupporterPaidIndicator(
  result: PlatformComparisonResult,
): SupporterPaidIndicator | undefined {
  if (!showSupporterExtra(result)) return undefined;

  if (hasOptionalTip(result)) {
    if (result.platformId === 'zeffy') {
      return {
        label: 'Suggested 15% contribution is added by default',
        detail: 'goes to Zeffy, not your organization',
      };
    }
    return {
      label: `Optional tips to ${result.platformName}`,
      detail: 'amount not estimated',
    };
  }

  const fundingKind = selectedFundingKind(result);
  if (!result.donorPaidExtraEstimated) {
    if (fundingKind === 'optional-contribution-to-organization') {
      return {
        label: 'Optional contribution to your organization',
        detail: 'amount not estimated',
      };
    }
    return {
      label:
        fundingKind === 'optional-fee-coverage'
          ? 'Supporters may cover fees'
          : 'Supporters may pay extra',
      detail: 'amount not estimated',
    };
  }

  if (fundingKind === 'donor-service-charge-to-organization') {
    return {
      label: 'Supporters pay',
      amountCents: result.donorPaidExtraCents,
      amountContext: 'in service fees',
      detail: 'service fee goes to your organization',
    };
  }

  if (fundingKind === 'optional-contribution-to-organization') {
    return {
      label: 'Optional contribution',
      amountCents: result.donorPaidExtraCents,
      amountContext: 'helps cover processing',
      detail: 'goes to your organization',
    };
  }

  return {
    label: 'Supporters pay',
    amountCents: result.donorPaidExtraCents,
    detail: 'covers fees',
  };
}
